import React from 'react';
import { Link } from 'react-router';
import type { LscVodHandoffSession } from '../types';
import {
  classifyVodHandoffSession,
  formatVodHandoffSessionLastChecked,
  formatVodHandoffSessionMatchLabel,
  getVodHandoffSessionBadges,
  getVodHandoffSessionNextAction,
  getVodHandoffSessionPrimaryText,
  selectPanelHandoffSessions,
  shouldShowVerifiedHandoffPanel,
  type VodHandoffSessionBadgeTone,
} from '../vodHandoffSessionTruth';

export type VerifiedHandoffAwaitingPanelProps = {
  leagueId: string;
  sessions: LscVodHandoffSession[];
  nowMs?: number;
};

const BADGE_TONE_STYLES: Record<VodHandoffSessionBadgeTone, string> = {
  success: 'bg-green-900/40 text-green-200 border-green-700',
  warning: 'bg-amber-900/40 text-amber-200 border-amber-700',
  danger: 'bg-red-900/40 text-red-200 border-red-800',
  neutral: 'bg-secondary-800 text-secondary-300 border-secondary-600',
};

function HandoffSessionRow({
  leagueId,
  session,
}: {
  leagueId: string;
  session: LscVodHandoffSession;
}) {
  const badges = getVodHandoffSessionBadges(session);
  const primaryText = getVodHandoffSessionPrimaryText(session);
  const nextAction = getVodHandoffSessionNextAction(session, leagueId);
  const matchLabel = formatVodHandoffSessionMatchLabel(session);
  const lastChecked = formatVodHandoffSessionLastChecked(session);
  const classification = classifyVodHandoffSession(session);
  const broadcaster = session.twitch_login?.trim();

  return (
    <article
      className="rounded-lg border border-secondary-700 bg-secondary-900/60 px-4 py-3 space-y-2"
      data-testid={`verified-handoff-session-${session.id}`}
      data-handoff-classification={classification}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          {matchLabel ? (
            <h3 className="text-sm font-medium text-white truncate">{matchLabel}</h3>
          ) : null}
          <p className="text-xs text-secondary-400">
            {broadcaster ? `@${broadcaster}` : 'Broadcaster unavailable'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          {badges.map((badge) => (
            <span
              key={badge.key}
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${BADGE_TONE_STYLES[badge.tone]}`}
              data-testid={`verified-handoff-badge-${badge.key}`}
            >
              {badge.label}
            </span>
          ))}
        </div>
      </div>

      {primaryText ? (
        <p className="text-sm text-secondary-300" data-testid="verified-handoff-primary-text">
          {primaryText}
        </p>
      ) : null}

      {classification === 'retry_pending' && lastChecked ? (
        <p className="text-xs text-secondary-500" data-testid="verified-handoff-last-checked">
          Last checked {lastChecked}
        </p>
      ) : null}

      {nextAction ? (
        nextAction.href ? (
          <Link
            to={nextAction.href}
            className="inline-flex text-sm font-medium text-primary-300 hover:text-primary-200"
            data-testid="verified-handoff-open-review"
          >
            {nextAction.label}
          </Link>
        ) : (
          <p
            className="text-xs text-secondary-500"
            data-testid="verified-handoff-next-action"
          >
            {nextAction.label}
          </p>
        )
      ) : null}
    </article>
  );
}

export function VerifiedHandoffAwaitingPanel({
  leagueId,
  sessions,
  nowMs,
}: VerifiedHandoffAwaitingPanelProps) {
  if (!shouldShowVerifiedHandoffPanel(sessions, nowMs)) {
    return null;
  }

  const visibleSessions = selectPanelHandoffSessions(sessions, nowMs);

  return (
    <section
      className="card space-y-4"
      data-testid="verified-handoff-awaiting-panel"
      aria-label="Suggested stream matches"
    >
      <div>
        <h2 className="text-lg font-semibold text-white">Live-to-archive waiting</h2>
        <p className="text-sm text-secondary-400 mt-1">
          Verified live games waiting for Twitch VODs or commissioner review. No action is needed
          while LSC is still waiting on Twitch.
        </p>
      </div>

      <div className="space-y-3">
        {visibleSessions.map((session) => (
          <HandoffSessionRow key={session.id} leagueId={leagueId} session={session} />
        ))}
      </div>
    </section>
  );
}

export default VerifiedHandoffAwaitingPanel;
